import { formatDistance, isoDateToLocalDate } from "./utils";
import {
  buildAssignmentInfoByMachine,
  getDaysSinceDate,
  getDaysSinceLastDoneVisit,
  getDaysSinceLastTaskOrAssignment,
  getDaysSinceTask,
  getLastTaskForMachine,
  getLastTaskForMachineSince,
  getLocationDaysSinceLastTask,
} from "./dayMetrics";
import { Task, TaskStatus, TaskType } from "./types";
import { centsOrZero, netRevenueCents } from "./currency";

export const isRouteTask = (task: Task) =>
  task.type !== "location_lead" || task.routable === true;

export function taskConfirmationPhoto(task?: Pick<Task, "assets"> | null) {
  return task?.assets
    ?.filter((asset) => asset.type === "confirmation_photo" && asset.url)
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))[0] || null;
}

type TaskDateField = "doneAt" | "scheduledFor" | "createdAt";

type TaskMatchOptions = {
  types?: TaskType[];
  statuses?: TaskStatus[];
  requireDone?: boolean;
  dateFieldOrder?: TaskDateField[];
};

const defaultDateOrder: TaskDateField[] = [
  "doneAt",
  "scheduledFor",
  "createdAt",
];

const toDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const TASK_STATUS_DOT_CLASSES: Record<TaskStatus, string> = {
  pending: "bg-amber-500",
  unassigned: "bg-blue-400",
  error: "bg-red-500",
  cancelled: "bg-orange-500",
  done: "bg-emerald-500",
};

export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  pending: "#f59e0b",
  unassigned: "#60a5fa",
  error: "#ef4444",
  cancelled: "#f97316",
  done: "#10b981",
};

export const USER_LOCATION_PIN_COLOR = "#1e3a8a";

export const TASK_STATUS_STACK_ORDER: TaskStatus[] = [
  "unassigned",
  "pending",
  "done",
  "cancelled",
  "error",
];

export const FINAL_TASK_STATUSES: TaskStatus[] = [
  "done",
  "cancelled",
  "error",
];

export const getOrderedTaskStatuses = (
  tasks: Array<Pick<Task, "status">>
): TaskStatus[] => {
  const statuses = new Set(tasks.map((task) => task.status).filter(Boolean));
  return TASK_STATUS_STACK_ORDER.filter((status) => statuses.has(status));
};

export const getPriorityTaskStatus = (
  tasks: Array<Pick<Task, "status">>
): TaskStatus | null => getOrderedTaskStatuses(tasks)[0] || null;

export const getTaskStatusMapPinColor = (
  tasks: Array<Pick<Task, "status">>
): string | undefined => {
  const status = getPriorityTaskStatus(tasks);
  return status ? TASK_STATUS_COLORS[status] : undefined;
};

export const isRefundTask = (task: Task) => task.type === "machine_refund";
export const isCollectionTask = (task: Task) =>
  task.type === "machine_collection";
export const shouldShowRefunds = (task: Task) =>
  isCollectionTask(task) || isRefundTask(task);
export const shouldShowGross = (task: Task) => isCollectionTask(task);
export const shouldShowCommission = (task: Task) => isCollectionTask(task);
export const shouldShowNet = (task: Task) => isCollectionTask(task);
export const getTaskCommissionEarnedCents = (task: Task) =>
  centsOrZero(task.commissionEarnedCents ?? task.commissionCents);
export const isTaskFinalStatus = (status?: TaskStatus | null) =>
  FINAL_TASK_STATUSES.includes(status as TaskStatus);
export const isTaskFinal = (task?: Pick<Task, "status"> | null) =>
  isTaskFinalStatus(task?.status);
export const isTaskCompleteStatus = (status?: TaskStatus | null) =>
  status === "done" || status === "cancelled";
export const isTaskComplete = (task?: Pick<Task, "status"> | null) =>
  isTaskCompleteStatus(task?.status);
export const isTaskInProgress = (
  task?: Pick<Task, "isLive"> | null,
) => Boolean(task?.isLive);
export const hasInProgressTasks = (
  tasks: Array<Pick<Task, "isLive">>,
) => tasks.some(isTaskInProgress);

export const getTaskDurationSeconds = (task?: Task | null) => {
  if (!task) return 0;
  if (typeof task.duration === "number" && task.duration > 0) {
    return task.duration;
  }
  if (!task.startedAt || !task.doneAt) return 0;
  const startedAt = new Date(task.startedAt);
  const doneAt = new Date(task.doneAt);
  const elapsedSeconds = (doneAt.getTime() - startedAt.getTime()) / 1000;
  if (Number.isNaN(elapsedSeconds)) return 0;
  return Math.max(0, elapsedSeconds);
};

export const formatTaskDuration = (task?: Task | null) => {
  const totalSeconds = Math.round(getTaskDurationSeconds(task));
  if (totalSeconds <= 0) return "—";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

export const formatTaskDistanceMiles = (task?: Task | null) => {
  return formatDistance(task?.distance);
};

export const getTaskFinancials = (task: Task, allTasks: Task[] = []) => {
  if (task.status === "cancelled") {
    return { grossCents: 0, refundsCents: 0, commissionCents: 0, netCents: 0 };
  }
  if (isRefundTask(task)) {
    return {
      grossCents: 0,
      refundsCents: centsOrZero(task.refundsCents),
      commissionCents: 0,
      netCents: -centsOrZero(task.refundsCents),
    };
  }
  if (!isCollectionTask(task)) {
    return { grossCents: 0, refundsCents: 0, commissionCents: 0, netCents: 0 };
  }
  if (!shouldUseRevenueValues(task, allTasks)) {
    return { grossCents: 0, refundsCents: 0, commissionCents: 0, netCents: 0 };
  }
  const gross = centsOrZero(task.grossCents);
  const refunds = centsOrZero(task.refundsCents);
  const commission = getTaskCommissionEarnedCents(task);
  const net =
    task.netCents !== undefined && task.netCents !== null
      ? task.netCents
      : netRevenueCents(gross, refunds, commission);
  return {
    grossCents: gross,
    refundsCents: refunds,
    commissionCents: commission,
    netCents: net,
  };
};

export const getRevenueFinancialTasks = (
  tasks: Task[],
  allTasks: Task[] = tasks
) =>
  Array.from(
    new Map(
      (tasks || [])
        .filter((task) => {
          if (!isCollectionTask(task) && !isRefundTask(task)) return false;
          if (isCollectionTask(task) && !shouldUseRevenueValues(task, allTasks)) {
            return false;
          }
          return Boolean(task.id);
        })
        .map((task) => [task.id, task])
    ).values()
  );


export const sumTaskFinancials = (tasks: Task[], allTasks: Task[] = []) => {
  const scopeTasks = allTasks.length ? allTasks : tasks;
  const revenueTasks = getRevenueFinancialTasks(tasks, scopeTasks);
  return revenueTasks.reduce(
    (acc, task) => {
      const totals = getTaskFinancials(task, scopeTasks);
      acc.grossCents += totals.grossCents;
      acc.refundsCents += totals.refundsCents;
      acc.commissionCents += totals.commissionCents;
      acc.netCents += totals.netCents;
      return acc;
    },
    { grossCents: 0, refundsCents: 0, commissionCents: 0, netCents: 0 }
  );
};

const isVisibleRevenueTask = (task: Task) =>
  task.type === "machine_collection";

export const getRevenueTask = (task: Task, tasks: Task[]): Task | null => {
  if (task.type === "machine_collection") return task;
  return null;
};

export const getRevenueOwnerTask = (task: Task, tasks: Task[]) => {
  if (isVisibleRevenueTask(task)) return task;
  return null;
};

const getTaskDate = (task: Task, order: TaskDateField[]): Date | null => {
  for (const field of order) {
    const date = toDate(task[field]);
    if (date) return date;
  }
  return null;
};

const startOfDay = (value: Date) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const matchesTask = (task: Task, options: TaskMatchOptions) => {
  if (options.types?.length && !options.types.includes(task.type)) return false;
  if (options.requireDone && task.status !== "done") return false;
  if (options.statuses?.length && !options.statuses.includes(task.status)) {
    return false;
  }
  return true;
};

export { getLastTaskForMachine, getLastTaskForMachineSince };

export const shouldUseRevenueValues = (task: Task, tasks: Task[]) => {
  if (!isVisibleRevenueTask(task)) return false;
  if (task.status === "done") return true;
  if (task.status === "cancelled") return false;
  const hasValues =
    centsOrZero(task.grossCents) !== 0 ||
    centsOrZero(task.refundsCents) !== 0 ||
    centsOrZero(task.commissionCents) !== 0 ||
    centsOrZero(task.netCents) !== 0;
  return hasValues;
};

export function buildLastTaskByMachine(
  tasks: Task[],
  options: TaskMatchOptions = {}
): Record<string, Task> {
  const order = options.dateFieldOrder || defaultDateOrder;
  const lastByMachine: Record<string, Task> = {};
  const lastDateByMachine: Record<string, Date> = {};
  for (const task of tasks) {
    if (!task.machineId) continue;
    if (!matchesTask(task, options)) continue;
    const taskDate = getTaskDate(task, order);
    if (!taskDate) continue;
    const existingDate = lastDateByMachine[task.machineId];
    if (!existingDate || taskDate > existingDate) {
      lastDateByMachine[task.machineId] = taskDate;
      lastByMachine[task.machineId] = task;
    }
  }
  return lastByMachine;
}

export function getLastCompletedTaskBefore(params: {
  tasks: Task[];
  machineId?: string | null;
  referenceDate: Date;
  excludeId?: string | null;
}): Task | null {
  const { tasks, machineId, referenceDate, excludeId } = params;
  if (!machineId) return null;
  const cutoff = startOfDay(referenceDate).getTime();
  let last: Task | null = null;
  let lastTime = -Infinity;
  for (const task of tasks) {
    if (task.machineId !== machineId) continue;
    if (excludeId && task.id === excludeId) continue;
    if (task.status !== "done") continue;
    const date = getTaskDate(task, defaultDateOrder);
    if (!date) continue;
    const time = date.getTime();
    if (time >= cutoff) continue;
    if (time > lastTime) {
      lastTime = time;
      last = task;
    }
  }
  return last;
}

export function getTaskReferenceDate(
  task: Task,
  fallbackDateIso: string,
  assignmentInfo?: Record<string, { assignedAt?: string | null }>
): Date {
  if (task.scheduledFor) {
    return isoDateToLocalDate(task.scheduledFor);
  }
  const assignedAt = task.machineId
    ? assignmentInfo?.[task.machineId]?.assignedAt
    : undefined;
  const assignedDate = assignedAt ? isoDateToLocalDate(assignedAt) : null;
  return assignedDate || isoDateToLocalDate(fallbackDateIso);
}

export { getDaysSinceTask, getDaysSinceDate, buildAssignmentInfoByMachine };

export { getLocationDaysSinceLastTask };

export function getLocationMaxDaysSinceLastTask(params: {
  tasks: Task[];
  machineIds: string[];
  assignmentInfo?: Record<string, { assignedAt?: string | null }>;
  referenceDate?: Date;
  includeReferenceDay?: boolean;
}): number | null {
  return getLocationDaysSinceLastTask({ ...params, mode: "max" });
}


export function getLocationDaysSinceLastService(params: {
  tasks: Task[];
  machineIds: string[];
  assignmentInfo?: Record<string, { assignedAt?: string | null }>;
  referenceDate?: Date;
  includeReferenceDay?: boolean;
}): number | null {
  return getLocationDaysSinceLastTask(params);
}
export {
  getDaysSinceLastDoneVisit,
  getDaysSinceLastTaskOrAssignment,
};

export function getNextScheduledTaskDate(params: {
  tasks: Task[];
  locationId?: string | null;
  machineId?: string | null;
  machineIds?: string[];
}): Date | null {
  const { tasks, locationId, machineId, machineIds } = params;
  const machineSet = new Set(
    [machineId, ...(machineIds || [])].filter(Boolean) as string[]
  );
  let nextDate: Date | null = null;
  for (const task of tasks) {
    if (task.status !== "pending" && task.status !== "unassigned") continue;
    if (!task.scheduledFor) continue;
    if (locationId && task.locationId && task.locationId !== locationId) {
      continue;
    }
    if (machineSet.size) {
      if (!task.machineId || !machineSet.has(task.machineId)) continue;
    } else if (locationId && !task.locationId) {
      continue;
    }
    const candidate = isoDateToLocalDate(task.scheduledFor);
    if (!nextDate || candidate.getTime() < nextDate.getTime()) {
      nextDate = candidate;
    }
  }
  return nextDate;
}

export function formatDaysSince(days: number | null, fallback = "—") {
  if (days === null) return fallback;
  return `${days} day${days === 1 ? "" : "s"}`;
}
